import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { flexRender, getCoreRowModel, getPaginationRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useModal } from '../../Contexts/ModalContext';
import SellerAddSale from './SellerAddSale';
import SellerEditableQuantity from './SellerEditableQuantity';

const SellerSales = ({ sales, representanteId, onChange }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { openModal } = useModal();

    const pageParams = new URLSearchParams(location.search);
    const page = Number(pageParams.get('page') ?? '1');

    const [pagination, setPagination] = useState({
        pageIndex: page - 1,
        pageSize: 12,
    });

    const isAllSelected = sales.length && sales.length > 0;

    const columns = useMemo(
        () => [
            { header: 'Mês', accessorKey: 'periodo', meta: { className: 'border p-4 text-left' } },
            { header: 'Quantidade', accessorKey: 'quantidade', meta: { className: 'border p-4 w-[17%] text-left' } },
        ],
        [isAllSelected]
    );

    const table = useReactTable({
        columns,
        data: sales,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        state: {
            pagination,
        },
        onPaginationChange: setPagination,
        autoResetPageIndex: false,
    });

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);

        if (pagination.pageIndex !== 0 || searchParams.has('page')) {
            searchParams.set('page', (pagination.pageIndex + 1).toString());
            navigate(`?${searchParams.toString()}`, { replace: true });
        }
    }, [pagination.pageIndex, navigate]);

    const openDeleteModal = (saleId) => {
        openModal({
            type: 'delete',
            confirm: `/manager/representantes/vendas/excluir/${saleId}`,
            onSuccess: () => onChange?.(),
            open: true,
        });
    };

    return (
        <>
            <div className="rounded-sm border border-stroke bg-white px-5 py-5 shadow">
                <h4 className="mb-6 text-xl font-bold text-black">Vendas mensais</h4>

                {representanteId && <SellerAddSale representanteId={representanteId} onSuccess={onChange} />}

                <div className="mt-6">
                    <table className="min-w-full border-collapse table-fixed">
                        <TableHead table={table} />
                        <tbody>
                            {table.getRowModel().rows.map((row, index) => {
                                const sale = row.original;
                                return (
                                    <tr className="bg-slate-50" key={sale.id}>
                                        <td className="border px-4 w-1/6 py-4">{index + 1}</td>
                                        <td className="border px-4 py-4 capitalize">{sale.periodo}</td>

                                        <SellerEditableQuantity sale={sale} />

                                        <td className="border px-4 py-4 w-1/6 text-center sort-ignore">
                                            <button onClick={() => openDeleteModal(sale.id)} className="h-5 w-5 relative z-[1]">
                                                <FontAwesomeIcon icon={faTrash} className="text-red-700" />
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                <Pagination pagination={pagination} table={table} />
            </div>
        </>
    );
};

export default SellerSales;

const TableHead = ({ table }) => {
    return (
        <thead>
            {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                    <th className="border p-4 w-1/6 text-left">#</th>
                    {headerGroup.headers.map((header) => {
                        return (
                            <th key={header.id} colSpan={header.colSpan} className={header.column.columnDef.meta?.className}>
                                <div
                                    {...{
                                        className: header.column.getCanSort() ? 'cursor-pointer select-none' : '',
                                        onClick: header.column.getToggleSortingHandler(),
                                    }}
                                >
                                    {flexRender(header.column.columnDef.header, header.getContext())}
                                    {{
                                        asc: (
                                            <svg
                                                className="fill-gray-500 inline-block mb-1 ml-1"
                                                width="10"
                                                height="5"
                                                viewBox="0 0 10 5"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path d="M5 0L0 5H10L5 0Z" fill=""></path>
                                            </svg>
                                        ),
                                        desc: (
                                            <svg
                                                className="fill-gray-500 inline-block mb-1 ml-1"
                                                width="10"
                                                height="5"
                                                viewBox="0 0 10 5"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path d="M5 5L10 0L-4.37114e-07 8.74228e-07L5 5Z" fill=""></path>
                                            </svg>
                                        ),
                                    }[header.column.getIsSorted()] ?? null}
                                </div>
                            </th>
                        );
                    })}
                    <th className="border p-4 w-1/6 text-left">Ações</th>
                </tr>
            ))}
        </thead>
    );
};

const Pagination = ({ pagination, table }) => {
    return (
        <div className="flex items-center mt-6 mb-4">
            <nav className="pagination isolate inline-flex space-x-1" aria-label="Pagination">
                <button
                    onClick={() => table.previousPage()}
                    disabled={!table.getCanPreviousPage()}
                    className={`relative inline-flex items-center justify-center rounded w-9 h-9 ring-1 ring-inset ring-gray-300 ${table.getCanPreviousPage() ? 'hover:text-white hover:bg-secondary hover:ring-secondary text-secondary' : 'text-slate-400'}`}
                >
                    <span className="sr-only">Anterior</span>
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" data-slot="icon">
                        <path
                            fillRule="evenodd"
                            d="M11.78 5.22a.75.75 0 0 1 0 1.06L8.06 10l3.72 3.72a.75.75 0 1 1-1.06 1.06l-4.25-4.25a.75.75 0 0 1 0-1.06l4.25-4.25a.75.75 0 0 1 1.06 0Z"
                            clipRule="evenodd"
                        ></path>
                    </svg>
                </button>

                {pagination.pageIndex !== 0 && (
                    <button
                        onClick={() => table.firstPage()}
                        disabled={!table.getCanPreviousPage()}
                        className={`relative inline-flex items-center justify-center font-medium rounded w-9 h-9 ring-1 ring-inset ring-gray-300 ${table.getCanPreviousPage() ? 'hover:text-white hover:bg-secondary hover:ring-secondary text-secondary' : 'text-slate-400'}`}
                    >
                        1
                    </button>
                )}

                {pagination.pageIndex > 1 && table.getPageOptions().length > 4 && (
                    <span className="relative inline-flex items-center justify-center font-medium rounded w-9 h-9 text-secondary ring-1 ring-inset ring-gray-300">
                        ...
                    </span>
                )}

                {table.getPageOptions()[pagination.pageIndex - 1] ? (
                    <button
                        onClick={() => table.previousPage()}
                        className="relative inline-flex items-center justify-center font-medium rounded w-9 h-9 text-secondary ring-1 ring-inset ring-gray-300"
                    >
                        {pagination.pageIndex}
                    </button>
                ) : null}

                <span className="relative inline-flex items-center justify-center font-medium rounded w-9 h-9 text-white bg-secondary ring-1 ring-inset ring-secondary hover:bg-secondary">
                    {pagination.pageIndex + 1}
                </span>

                {table.getPageOptions()[pagination.pageIndex + 1] && (
                    <button
                        onClick={() => table.setPageIndex(pagination.pageIndex + 1)}
                        className="relative inline-flex items-center justify-center font-medium rounded w-9 h-9 text-secondary ring-1 ring-inset ring-gray-300"
                    >
                        {pagination.pageIndex + 2}
                    </button>
                )}

                {pagination.pageIndex < table.getPageOptions().length - 3 && (
                    <span className="relative inline-flex items-center justify-center font-medium rounded w-9 h-9 text-gray-400 ring-1 ring-inset ring-gray-300">
                        ...
                    </span>
                )}

                {pagination.pageIndex < table.getPageOptions().length - 2 && (
                    <button
                        onClick={() => table.lastPage()}
                        className="relative inline-flex items-center justify-center font-medium rounded w-9 h-9 text-secondary ring-1 ring-inset ring-gray-300"
                    >
                        {table.getPageOptions().length}
                    </button>
                )}

                <button
                    onClick={() => table.nextPage()}
                    disabled={!table.getCanNextPage()}
                    className={`relative inline-flex items-center justify-center rounded w-9 h-9 ring-1 ring-inset ring-gray-300 ${table.getCanNextPage() ? 'hover:text-white hover:bg-secondary hover:ring-secondary text-secondary' : 'text-slate-400'}`}
                >
                    <span className="sr-only">Próximo</span>
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" data-slot="icon">
                        <path
                            fillRule="evenodd"
                            d="M8.22 5.22a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 0 1-1.06-1.06L11.94 10 8.22 6.28a.75.75 0 0 1 0-1.06Z"
                            clipRule="evenodd"
                        ></path>
                    </svg>
                </button>
            </nav>
        </div>
    );
};
